import { observer } from 'mobx-react'
import React, { Component, Fragment } from 'react'
import styled from 'styled-components'
import BigImage from '../../components/BigImage'
import Paginate from '../../components/Paginate'
import Prompts from '../../components/Prompts'
import SectionHeader from '../../components/SectionHeader'
import Paragraph from '../../components/Paragraph'
import patient from '../../stores/patient'
import MotivationToggles from './MotivationToggles'
import PostStrengthsPrompts2 from './PostStrengthsPrompts2'
import StrengthToggles from './StrengthToggles'
import LifestyleFaqSection, { RichAnswer } from "../../components/LifeStyleFaqSection";
import ImageComparisonSlider from "../../components/ImageComparisonSlider";
import Text from "../../components/Text";
import Card from "../../components/Card";
import { familyMembersFaq, medicationEffectivenessFaq } from "../../utils/lifeStyleFaqs";
import LifestyleSummary from '../../components/LifestyleSummary'
import getLifestyleSummaryValues from '../../utils/getLifestyleSummaryValues'

const FaqImages = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1.25rem;
`

const FaqImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  margin: 0 auto;
  border: 1px solid var(--light-gray);
  border-radius: 10px;
`

const FamilyImageWrap = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto;
`

const FamilyImage = styled(FaqImage)`
  margin: 0;
`

const FamilyImageText = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`

const GraphicVersionLabel = styled.h3`
  margin: 2rem 0 0.75rem;
  color: #2f6fb3;
  font-size: 1.35rem;
  font-weight: 700;
  text-align: center;
`

const getFamilyScreeningAge = diagnosisAge => {
  const age = parseInt(diagnosisAge, 10)
  return !Number.isNaN(age) && age < 50 ? age - 10 : 40
}

const getDiagnosisAgeFromQuery = () => (
  new URLSearchParams(window.location.search).get('diagnosisAge') || ''
)

@observer
export default class GoalSession2 extends Component {
  renderMedicationEffectivenessSection = graphicVariant => (
    <Fragment>
      <GraphicVersionLabel>
        Graphic {graphicVariant}
      </GraphicVersionLabel>

      <SectionHeader
        color="red"
        icon="fas fa-prescription-bottle-alt"
        str={medicationEffectivenessFaq.question}
      />

      <Card>
        <Card.Pad>
          <RichAnswer
            blocks={medicationEffectivenessFaq.answer}
            graphicVariant={graphicVariant}
          />
        </Card.Pad>
      </Card>
    </Fragment>
  )

  renderLifestyleSection = (graphicVariant, beforeSrc, afterSrc) => (
    <Fragment>
      <GraphicVersionLabel>
        Graphic {graphicVariant}
      </GraphicVersionLabel>

      <SectionHeader
        color="red"
        icon="fas fa-leaf"
        str="titles.lifestyleAndGlaucoma"
      />

      <Card>
        <Card.Pad>
          <Paragraph>
            <strong>
              <Text str="Does my lifestyle affect glaucoma?" />
            </strong>
          </Paragraph>

          <Paragraph>
            <Text str="Lifestyle factors can " />
            <strong>
              <Text str="decrease your risk of developing glaucoma." />
            </strong>
            <Text str=" However, once you are diagnosed with glaucoma, lifestyle changes " />
            <strong>
              <Text str="do not stop the disease from worsening." />
            </strong>
          </Paragraph>

          <Paragraph>
            <Text str="Scientific studies show that " />
            <strong>
              <Text str="only treatments that lower eye pressure — medications, laser therapy, or surgery — have been proven to preserve vision" />
            </strong>
            <Text str=" in people with glaucoma." />
          </Paragraph>
        </Card.Pad>
      </Card>

      <ImageComparisonSlider
        beforeSrc={beforeSrc}
        afterSrc={afterSrc}
        beforeAlt={`Graphic ${graphicVariant} glaucoma prevention`}
        afterAlt={`Graphic ${graphicVariant} glaucoma treatment`}
        label={`Compare Graphic ${graphicVariant} prevention and treatment`}
        caption=""
      />
    </Fragment>
  )

  renderOriginalFamilyGraphic = familyScreeningAge => (
    <FaqImages>
      <FamilyImageWrap>
        <FamilyImage
          alt={`Family history increases glaucoma risk from 2 percent to 20 percent. Family members should get screened when they are ${familyScreeningAge} years old.`}
          src="/images/faq/family.png"
        />
        <FamilyImageText
          aria-hidden="true"
          focusable="false"
          viewBox="0 0 1601 1783"
        >
          <rect x="645" y="245" width="930" height="115" fill="#fff" />
          <text
            fill="#f00"
            fontFamily="Arial, Helvetica, sans-serif"
            fontSize="52"
            textAnchor="middle"
            x="1060"
            y="325"
          >
            increases from 2% to 20%.
          </text>
          <rect x="95" y="1165" width="765" height="430" fill="#fff" />
          <text
            fill="#000"
            fontFamily="Arial, Helvetica, sans-serif"
            fontSize="52"
            x="145"
            y="1255"
          >
            <tspan x="145">It is important that your</tspan>
            <tspan x="145" dy="74">family members also</tspan>
            <tspan x="145" dy="74">meet with an eye doctor</tspan>
            <tspan x="145" dy="74">to get screened when</tspan>
            <tspan x="145" dy="74">they are {familyScreeningAge} years old.</tspan>
          </text>
        </FamilyImageText>
      </FamilyImageWrap>
    </FaqImages>
  )

  renderAlternativeFamilyGraphic = familyScreeningAge => (
    <FaqImages>
      <FamilyImageWrap>
        <FamilyImage
          src="/images/faq/comparison/family-risk-round-2-1.png"
          alt={`Family history can increase glaucoma risk from 2 percent to 20 percent. Family members should get screened when they are ${familyScreeningAge} years old.`}
        />

        <FamilyImageText
          aria-hidden="true"
          focusable="false"
          viewBox="0 0 1541 2200"
        >
          <rect x="650" y="1570" width="870" height="330" fill="#fbfcf0" />
          <text
            fill="#001744"
            fontFamily="Arial, Helvetica, sans-serif"
            fontSize="48"
            fontWeight="700"
            x="700"
            y="1640"
          >
            <tspan x="700">It is important that your family</tspan>
            <tspan x="700" dy="64">members also meet with an</tspan>
            <tspan x="700" dy="64">eye doctor to get screened</tspan>
            <tspan x="700" dy="64">
              when they are {familyScreeningAge} years old.
            </tspan>
          </text>
        </FamilyImageText>
      </FamilyImageWrap>
    </FaqImages>
  )

  renderFamilyRiskSection = (graphicVariant, familyScreeningAge) => (
    <Fragment>
      <GraphicVersionLabel>Graphic {graphicVariant}</GraphicVersionLabel>
      <SectionHeader color="red" icon="fas fa-users" str="Family Risk" />

      <Card>
        <Card.Pad>
          <RichAnswer blocks={familyMembersFaq.answer} />

          {graphicVariant === 'A'
            ? this.renderOriginalFamilyGraphic(familyScreeningAge)
            : this.renderAlternativeFamilyGraphic(familyScreeningAge)}

          <Paragraph>
            Please let your family members – brothers, sisters, parents, and
            children – know that you have glaucoma so that they can be proactive.
            They can get checked by an eye doctor to begin treatment, if needed,
            early on, when the disease is easier to control. And they can make
            healthier lifestyle choices to lower their risk of developing
            glaucoma if they want.
          </Paragraph>
        </Card.Pad>
      </Card>
    </Fragment>
  )

  render() {
    let sliderBeforeSrc = ''
    let sliderAfterSrc = ''
    const diagnosisAge = patient.fields.diagnosisAge || getDiagnosisAgeFromQuery()
    const familyScreeningAge = getFamilyScreeningAge(diagnosisAge)
    const importanceScore = Number(patient.fields.importance)
    const shouldUsePriorMotivationPrompt = !Number.isNaN(importanceScore) && importanceScore <= 6
    const confidenceScore = Number(patient.fields.confidence)
    const shouldUsePriorStrengthPrompt = !Number.isNaN(confidenceScore) && confidenceScore <= 6
    const {
      motivationValues,
      strengthValues,
      qualityValues,
    } = getLifestyleSummaryValues(patient.fields)

    if(patient.fields.gender === 'MALE') {
      if(patient.fields.ethnicity === 'BLACK_AFRICAN_AMERICAN') {
        sliderBeforeSrc='/images/faq/slider/pmc.png'
        sliderAfterSrc = '/images/faq/slider/tmc.png'
      } else {
        sliderBeforeSrc='/images/faq/slider/pmw.png'
        sliderAfterSrc = '/images/faq/slider/tmw.png'

      }
    } else if(patient.fields.gender === 'FEMALE') {
      if(patient.fields.ethnicity === 'BLACK_AFRICAN_AMERICAN') {
        sliderBeforeSrc='/images/faq/slider/pwc.png'
        sliderAfterSrc = '/images/faq/slider/twc.png'
      } else {
        sliderBeforeSrc='/images/faq/slider/pww.png'
        sliderAfterSrc = '/images/faq/slider/tww.png'
      }

    }
    return (
      <Fragment>
        <Prompts>
          <Prompts.Q str="prose.itsGreatToSeeYou" />
          <Prompts.R str="prose.itSoundsLike" />
          <Prompts.S>
            <Paragraph str="prose.knowYouALittleBetter" />
            <Paragraph str="prose.lastTimeWeMet" />
          </Prompts.S>
          <Prompts.Q str="prose.tellMeHowItsGone" />
          <Prompts.R str="prose.youHave" />
        </Prompts>
        {/* MOTIVATIONS */}
        <SectionHeader color="red" icon="fas fa-handshake" str="titles.motivations" />
        <BigImage src="/images/family-hugging.jpg" />
        <Prompts>
          {shouldUsePriorMotivationPrompt &&
            <Prompts.Q
              str="prose.lastTimeValuesMaintainingVision"
              inserts={{ values: motivationValues }}
            />
          }
          {!shouldUsePriorMotivationPrompt && patient.fields.lowVision==="YES" && <Prompts.Q str="prose.ifItsOkayLowVision" />}
          {!shouldUsePriorMotivationPrompt && patient.fields.lowVision!=="YES" && <Prompts.Q str="prose.ifItsOkay" />}
        </Prompts>
        <MotivationToggles />
        <Prompts>
          {!shouldUsePriorMotivationPrompt &&
            <Prompts.Q
              str="prose.itSoundsLikeBlank"
              inserts={{ values: motivationValues }}
            />
          }
          <Prompts.R str={shouldUsePriorMotivationPrompt ? "prose.maintainVisionNecessary" : "prose.imHearingThat"} />
        </Prompts>
        {/* STRENGTHS */}
        <SectionHeader color="red" icon="fas fa-heart" str="titles.strengths" />
        <BigImage src="/images/roadmap.jpg" />
        <Prompts>
          {shouldUsePriorStrengthPrompt &&
            <Prompts.Q
              str="prose.lastTimeStrengthsInfluencedDrops"
              inserts={{ strengths: strengthValues, qualities: qualityValues }}
            />
          }
          {!shouldUsePriorStrengthPrompt && patient.fields.lowVision==="YES" && <Prompts.Q str="prose.weHaveFoundThatLowVision" />}
          {!shouldUsePriorStrengthPrompt && patient.fields.lowVision!=="YES" && <Prompts.Q str="prose.weHaveFoundThat" />}
        </Prompts>
        <StrengthToggles />
        <PostStrengthsPrompts2 />
        {this.renderMedicationEffectivenessSection('A')}
        {this.renderMedicationEffectivenessSection('B')}
        <Prompts>
          <Prompts.Q str="prose.whatIfAnythingDoesThisBringUp" />
          <Prompts.R str="prose.itEllipsis" />
          <Prompts.P str="prose.nextLetsTalkFamilyRisk" />
        </Prompts>
        {this.renderFamilyRiskSection('A', familyScreeningAge)}
        {this.renderFamilyRiskSection('B', familyScreeningAge)}
        <Prompts>
          <Prompts.Q str="prose.whatIfAnyFeelingsDoesThisBringUp" />
          <Prompts.R str="prose.youFeelEllipsis" />
          <Prompts.P str="prose.nextTalkLifestyleChanges" />
        </Prompts>
        {this.renderLifestyleSection(
          'A',
          sliderBeforeSrc,
          sliderAfterSrc
        )}

        {this.renderLifestyleSection(
          'B',
          '/images/faq/comparison/lifestyle-prevention-round-2.png',
          '/images/faq/comparison/lifestyle-treatment-round-2.png'
        )}
        <Prompts>
          <Prompts.Q str="prose.nextReviewGlaucomaFaqs" />
        </Prompts>
        <SectionHeader color="red" icon="fas fa-question" str="titles.faq"/>
        <LifestyleFaqSection
          diagnosis={patient.fields.diagnosis}
          gender={patient.fields.gender}
        />
        <LifestyleSummary
          color="red"
          motivationValues={motivationValues}
          strengthValues={strengthValues}
          qualityValues={qualityValues}
        />
        <Prompts>
          <Prompts.Q str="prose.whatQuestionsRegardingSharedInfo" />
          <Prompts.R str="prose.questionYouHaveDoctor" />
          <Prompts.P str="prose.nextAreaEyeDropsGoing" />
        </Prompts>
        {/* PAGINATE */}
        <Paginate>
          <Paginate.Button next
            color="orange"
            str="titles.use"
            to={`/use/second/${patient.fields._id}`}
          />
        </Paginate>
      </Fragment>
    )
  }
}



// WEBPACK FOOTER //
// ./src/pages/Goal/GoalSession2.js